import DatabaseModel from './DatabaseModel.js';
import Utils from '../Utils.js';
import { EventBus } from '../EventBus.js';
import { unwrapDto } from './responseUtils.js';

/**
 * Las llamadas de las pantallas del solicitante (#58): «Mis vacaciones», el formulario de solicitud
 * y el detalle de un día de vacaciones del calendario.
 *
 * Está aparte de `RhVacaciones.js`, que lleva la tarjeta de la ficha y el Mapa de aprobación (#52):
 * son dos pantallas distintas, de dos personas distintas, y meterlas en el mismo fichero deja un
 * módulo en el que no se sabe qué usa quién.
 *
 * El cálculo, el saldo y las validaciones **no están aquí**: los hace el servidor (#57) y estas
 * funciones solo preguntan. Si un número no cuadra, se arregla allí.
 */
const RhSolicitudesVacaciones = Object.create(DatabaseModel);

RhSolicitudesVacaciones.model = 'rh/mis-vacaciones';
RhSolicitudesVacaciones.modelFront = 'vacaciones';

/** La raíz de los endpoints de la #57, que son los que piden y anulan. */
const SOLICITUDES = 'rh/vacaciones';

/** Y la de las normas de vacaciones (#56). Ver `normasVigentes`. */
const NORMAS = 'rh/normas-vacaciones';

// ---------------------------------------------------------------- de quién es cada respuesta

/**
 * **Cuántas veces se ha cerrado una sesión** desde que se cargó la aplicación: la generación que
 * distingue dos sesiones de la misma cuenta.
 *
 * Cuelga de `app:session:close` y no de `app:session:open` a propósito: en la aplicación de Cordova
 * la sesión **se renueva** sola con las credenciales guardadas (`app.vue`), y eso cambia el token
 * sin que haya cambiado quién está delante. `removeSession` es el único camino que cierra una.
 *
 * Es el mismo criterio que ya decidió la casa para el PDF del control diario (`FactCobro.js`, #173
 * y #262): por eso está escrito igual y no de otra manera.
 */
let cierresDeSesion = 0;

/** Quién quiere enterarse del cierre. Ver `alCerrarSesion`. */
const avisarDelCierreA = [];

EventBus.$on('app:session:close', function() {
    cierresDeSesion += 1;
    // Lo primero que se tira son los «¿Continuar?» que haya abiertos de una acción diferida: son lo
    // único de este módulo que se queda PINTADO sobre la pantalla siguiente, así que esperar al
    // aviso de las pantallas sería dejarlo a la vista mientras tanto. Ver `preguntarEnEstaSesion`.
    cerrarLosDialogosDeOtraSesion();
    // Y los avisos van DESPUÉS de subir el sello, nunca antes: lo que hace cada pantalla al
    // enterarse —tirar lo que tenga en vuelo— se decide comparando `sesionActual()` con la sesión
    // con la que se cargó, así que si se avisara primero el sello todavía diría que es la misma y
    // no se tiraría nada. Por eso el aviso sale de aquí y no de un segundo `EventBus.$on` en cada
    // pantalla: así el orden no depende de quién se suscribiera antes.
    avisarDelCierreA.slice().forEach(function(queHacer) {
        try {
            queHacer();
        } catch (e) {
            // Lo que le falle a una pantalla no puede dejar a las demás sin enterarse.
            console.error('[RH-VAC] Fallo al avisar del cierre de sesión', e);
        }
    });
});

/**
 * **EL ÚNICO SITIO QUE AVISA A LAS PANTALLAS DE ESTE MÓDULO DE QUE SE HA CERRADO LA SESIÓN** (#58).
 *
 * Devuelve la función que da de baja el aviso, para llamarla al destruir la pantalla.
 *
 * Hace falta porque las rutas del módulo son `keepAlive`: cerrar sesión no destruye «Mis
 * vacaciones», ni su popup de solicitud, ni el calendario, así que **nada se enteraba hasta la
 * siguiente entrada en la ruta** (`page:beforein`). Mientras tanto, lo que estuviera en vuelo
 * seguía siendo «la petición vigente» de su carril: su fallo podía abrir un aviso encima del login
 * o de otra pantalla, y su final apagaba el «cargando» —que es un modal de la aplicación— de lo que
 * hubiera delante (revisión CODEX del PR #1733, ronda 7).
 *
 * El sello del adaptador ya cambiaba en ese momento, pero un sello que nadie mira no invalida nada:
 * quien tiene que tirar sus lecturas es cada pantalla, en cuanto pasa. Lo que vive aquí es **el
 * aviso**, para que ninguna pantalla del módulo tenga que acordarse de en qué momento pasa ni de en
 * qué orden.
 *
 * La pieza común de los carriles (`carrilPeticiones`) **no se toca**: es la decisión de dirección
 * del 09/10/2026 en esta misma incidencia —el arreglo de raíz, que se encargue ella por las once
 * pantallas que la usan, va en una ficha aparte—.
 */
RhSolicitudesVacaciones.alCerrarSesion = function(queHacer) {
    avisarDelCierreA.push(queHacer);
    return function() {
        const donde = avisarDelCierreA.indexOf(queHacer);
        if (donde !== -1) avisarDelCierreA.splice(donde, 1);
    };
};

/**
 * **La sesión que está delante**, con forma de sello opaco: dos sellos distintos son dos sesiones
 * distintas y no hay nada más que leer dentro.
 *
 * Las tres mitades son las del sello de `FactCobro.sesionActual`, y cada una tapa un caso:
 *
 *  - si hay sesión abierta o no (cerrar sesión y no entrar con nadie también es un cambio);
 *  - qué cuenta es (dos personas en el mismo dispositivo);
 *  - cuántos cierres ha habido (la misma cuenta que sale y vuelve a entrar devolvería el sello
 *    anterior, y entonces lo que pidió la sesión cerrada se daría por bueno).
 *
 * Y **no lleva el token** a propósito: la renovación automática de Cordova no es otra sesión, y
 * metiéndolo se descartaría la respuesta de una solicitud que la persona sí había enviado.
 *
 * Público porque la pantalla lo necesita para lo mismo —nombrar con qué sesión se cargó lo que
 * enseña— y leerlo dos veces, cada uno a su manera, es tener dos vigilantes que no se hablan.
 */
RhSolicitudesVacaciones.sesionActual = function() {
    const usuario = Utils.getUser();
    const cuenta = (usuario && usuario.username) || '';
    return (Utils.getToken() ? 'abierta|' : 'cerrada|') + cuenta + '|' + cierresDeSesion;
};

/** El rechazo de lo que ya no es de quien lo pidió: sin mensaje, porque no hay nada que enseñar. */
function rechazoDeOtraSesion() {
    return { status: 0, code: null, message: null, otraSesion: true };
}

/** El de un intento repetido de algo que ya está en marcha. Tampoco hay nada que enseñar. */
function rechazoYaEnVuelo() {
    return { status: 0, code: null, message: null, yaEnVuelo: true };
}

/**
 * **Si un rechazo es de los que no hay que contarle a nadie**, que son los dos que salen de aquí:
 * la respuesta de otra sesión y el intento repetido de algo que ya está en marcha.
 *
 * Lo pregunta quien lanza una escritura: su `catch` tiene que soltar lo que tenga cogido —el reloj,
 * el cerrojo de la pantalla— pero no escribir un aviso de algo que no ha pasado. Es **una sola
 * pregunta** para los dos casos a propósito: con un predicado por motivo, el `catch` que se
 * olvidara de uno volvería a enseñar el aviso que esto evita.
 */
RhSolicitudesVacaciones.sinNadaQueDecir = function(e) {
    return !!(e && (e.otraSesion || e.yaEnVuelo));
};

/**
 * **EL ÚNICO SITIO QUE ATA UNA ESCRITURA DE ESTE MÓDULO A LA SESIÓN QUE LA LANZÓ** (#58).
 *
 * Envuelve la promesa de una escritura y deja pasar su respuesta —el éxito y el fallo, los dos—
 * solo si sigue habiendo delante la misma sesión que la pidió.
 *
 * Hace falta porque la ruta de «Mis vacaciones» es `keepAlive`: cerrar sesión no destruye la
 * pantalla ni el popup de solicitud, así que el `then` de un POST en vuelo se ejecutaba sobre la
 * sesión siguiente. En la práctica, quien entrara después veía un aviso con el **código de
 * solicitud de la persona anterior**, se le cerraba el formulario que hubiera abierto y se le
 * recargaba la pantalla.
 *
 * Las **lecturas** de la ventana no pasan por aquí porque ya están atadas por el otro extremo: van
 * por un carril de peticiones con su sello de sesión (`carrilPeticiones`, decisión de dirección del
 * 06/10/2026), que las descarta antes de llegar a sus manejadores **en cuanto su pantalla le pone
 * el sello nuevo**, y de que eso pase al cerrar sesión —y no en la siguiente entrada en la ruta—
 * responde `alCerrarSesion`. Las escrituras no tenían ni lo uno ni lo otro, y son las que dejan
 * algo hecho aunque su respuesta se tire (revisión CODEX del PR #1733, ronda 10).
 *
 * Toda escritura nueva del módulo (aprobar y denegar son de la #59, pedir anulación de la #61) se
 * ata pasándola por aquí: es una línea, y es lo que evita que la garantía vuelva a quedarse
 * repartida entre los `then` de cada pantalla.
 *
 * Y se **exporta** por lo mismo: aprobar y denegar viven en otro adaptador —son de otra pantalla y
 * de otra persona (`RhBandejaVacaciones`)— y salieron sin pasar por aquí, que es exactamente lo que
 * este comentario preveía. Copiarlo allí daría dos cerrojos con dos cuentas de escrituras y dos
 * sellos: el sitio sigue siendo UNO y lo importa quien escriba (17/10/2026, #59).
 */
export function deLaSesionQueLaLanzo(promesa) {
    const sesionQuePide = RhSolicitudesVacaciones.sesionActual();
    const sigueSiendoSuya = function() {
        return RhSolicitudesVacaciones.sesionActual() === sesionQuePide;
    };
    return promesa.then(
        function(res) {
            return sigueSiendoSuya() ? res : Promise.reject(rechazoDeOtraSesion());
        },
        function(e) {
            return Promise.reject(sigueSiendoSuya() ? e : rechazoDeOtraSesion());
        }
    );
}

/**
 * **EL ÚNICO SITIO QUE ATA UNA ACCIÓN DIFERIDA A LA SESIÓN QUE LA EMPEZÓ** (#70).
 *
 * Es el **espejo** de `deLaSesionQueLaLanzo`: aquella descarta una respuesta que llega cuando ya hay
 * otra sesión delante; esta impide que **salga** una petición que empezó en la anterior.
 *
 * ## Qué es una acción diferida, y por qué hace falta
 *
 * Una acción que no sale al pulsar, sino después de que la persona conteste a uno o varios
 * «¿Continuar?». El registro en nombre de otra persona tiene dos —el mes cerrado y el saldo que
 * queda en rojo— y la anulación tiene uno.
 *
 * Esos diálogos **no son de la pantalla**: los monta el marco de Framework7 por
 * encima de todo, así que ni `dejarDeMandar` ni ninguna otra salida de `soloMandaLaDeDelante` los
 * alcanza —ahí está escrito por qué: la pantalla solo puede tirar lo que posee—. Si la sesión se cae
 * con el diálogo abierto, al pulsar «Continuar» la cadena seguía llegando al POST con **la persona,
 * las fechas, el motivo y las confirmaciones de la sesión anterior**, y el sello se tomaba recién
 * dentro de `guardar()`, o sea ya con la cuenta nueva delante: si quien entraba también era ADMIN o
 * DIRECTOR, el servidor lo aceptaba y quedaba guardado así (revisión CODEX del PR #1737, ronda 8,
 * r4302182229).
 *
 * ## El contrato
 *
 * La acción se **nombra antes del primer diálogo** y lleva dentro el sello —con su generación, que
 * es lo que distingue dos sesiones de la misma cuenta— y **los datos y las confirmaciones de
 * entonces**. Cada continuación y el punto común previo al POST preguntan si sigue vigente
 * (`sigueSiendoDeEstaSesion`) y, si no, **no se guarda nada y no se dice nada**: el aviso era para
 * quien lo empezó, igual que los rechazos de `sinNadaQueDecir`.
 *
 * Lo que **no** se hace: renovar el sello al guardar —sería tomarlo de la sesión que no empezó la
 * acción— ni reutilizar el estado que haya en pantalla en ese momento, que puede ser de otra cuenta.
 *
 * Vive aquí, en el adaptador, y no en un envoltorio de diálogos para toda la aplicación: eso es la
 * misma clase de pieza compartida que dirección dejó fuera el 09/10/2026 (ver `alCerrarSesion`).
 * Esto es del módulo, y es una línea en cada acción que se aplace: la de la #59 y la de la #61
 * nacen cubiertas pasando por aquí.
 */
RhSolicitudesVacaciones.accionDeEstaSesion = function (datos) {
    return { sesion: RhSolicitudesVacaciones.sesionActual(), datos: datos };
};

/**
 * Si esa acción diferida sigue siendo de la sesión que está delante. Lo pregunta **cada**
 * continuación y el punto común previo al POST: una sola comprobación al final dejaría pasar lo que
 * se decidiera entre un diálogo y el siguiente.
 */
RhSolicitudesVacaciones.sigueSiendoDeEstaSesion = function (accion) {
    return !!accion && accion.sesion === RhSolicitudesVacaciones.sesionActual();
};

/** Los «¿Continuar?» que hay abiertos ahora mismo, con la acción a la que pertenece cada uno. */
const dialogosDeAccionesDiferidas = [];

/**
 * **Los rótulos de los dos botones de un «¿Continuar?» del módulo**, en español y en un solo sitio.
 *
 * Están aquí y no en cada pantalla por lo mismo que el resto de este fichero: este es el único sitio
 * que pregunta, así que el «¿Continuar?» que añada la #59 o la #61 nace rotulado sin que nadie se
 * acuerde. Y no pasan por `$t` porque esto no es un componente —no hay `$t` que llamar— y el módulo
 * entero existe solo en español; el día que haya un segundo idioma, el sitio al que hay que traer la
 * traducción es este y solo este.
 *
 * «Continuar» y no «Aceptar» porque las dos preguntas acaban en «¿Continuar?»: el botón repite la
 * palabra de la pregunta (dirección, 18/10/2026).
 */
const ROTULOS_CONTINUAR = { cancelar: 'Cancelar', continuar: 'Continuar' };

/**
 * **EL ÚNICO SITIO QUE PREGUNTA «¿CONTINUAR?» POR UNA ACCIÓN DIFERIDA** (#70).
 *
 * Es la otra mitad de `accionDeEstaSesion`, y cierra lo que aquella no podía: que la acción no SALGA
 * con la sesión caída ya estaba resuelto, pero el diálogo **seguía pintado**. Con él delante se leen
 * el nombre de la persona y su saldo en rojo sobre el login o sobre la cuenta que acaba de entrar,
 * que es un dato de alguien a quien quien está delante no tiene por qué ver (revisión CODEX del PR
 * #1737, r4302931769).
 *
 * `app.dialog.create(…).open()` devuelve la instancia del diálogo, que es lo que hay que cerrar:
 * Framework7
 * los monta como vistas temporales por encima de todo y no los cierra nadie
 * (https://v5.framework7.io/docs/dialog). La pantalla no los posee —por eso `dejarDeMandar` nunca
 * los alcanzó—, pero **la acción sí**: el diálogo se apunta aquí al abrirlo y se cierra solo cuando
 * la sesión deja de ser la que empezó la acción.
 *
 * Vive aquí, en la pieza de sesión/acción que ya existe, y **no** en un envoltorio de diálogos para
 * toda la aplicación: eso es lo que dirección dejó fuera el 09/10/2026 y lo que @Donchurru repitió
 * el 17/10/2026. Así, el «¿Continuar?» que añada la #59 o la #61 nace cubierto por pasar por aquí.
 *
 * ## Y quien pregunta tiene que poder soltar lo que cogió
 *
 * Una acción diferida coge el cerrojo de su botón **al nombrarse**, antes del primer diálogo: entre
 * la pulsación y el POST hay dos «¿Continuar?» y, cogiéndolo solo en el POST, dos pulsaciones abren
 * dos cadenas de diálogos con los mismos datos —y si la primera termina antes de que se conteste la
 * segunda, el cerrojo del adaptador (`unaSolaEnVuelo`) ya está suelto y la segunda guarda otra vez
 * (revisión CODEX del PR #1737, r4303373381)—. Por eso cada cadena que **no** continúa tiene que
 * avisar: `alAbandonar` se llama **siempre que esto no llama a `alContinuar`** —al cancelar, y en
 * las dos comprobaciones de sesión—, así que el llamante suelta su cerrojo en un solo sitio y no
 * tiene que acordarse de cada salida. Soltarlo ahí es seguro porque no hay ningún POST en vuelo:
 * lo que no se puede soltar desde fuera es el cerrojo de un registro que ya salió, y de eso
 * responde `concluirRegistro` con su testigo.
 *
 * ## Y una cadena termina cuando el diálogo se cierra, no cuando se pulsa uno de sus botones
 *
 * Eso de «siempre que esto no llama a `alContinuar`» se escribió enumerando las salidas que se
 * conocían —continuar, cancelar y las dos comprobaciones de sesión—, y **un diálogo se puede cerrar
 * sin pasar por ninguna**: en Android, el botón físico Atrás llama a `f7.dialog.close()` desde
 * `cordova-app.js`, que cierra el `.dialog.modal-in` que haya delante sin disparar el callback de
 * ningún botón. Así, la cadena se quedaba sin terminar nunca: una anulación dejaba la lista entera
 * sin poder anular nada y el registro quedaba con `registrando` puesto incluso después de cerrar y
 * reabrir el popup en la misma sesión (hallazgo r4303600219).
 *
 * Es la tercera vez que sale la MISMA forma —el fin de un «¿Continuar?» atendido solo por sus
 * botones: primero el diálogo que nadie cerraba al caer la sesión (r4302931769), después el cerrojo
 * que se cogía demasiado tarde (r4303373381) y ahora el que no se suelta—, así que en vez de añadir
 * la salida que falta se cambia **de quién se escucha**: la cadena se resuelve **UNA vez** y lo que
 * la da por terminada es el ciclo de vida del diálogo (`closed`), que ocurre por cualquier causa
 * —Atrás, `close()` desde fuera, o el botón que sea—, no la lista de causas que alguien recordara.
 * Con eso, una salida nueva no hay que preverla.
 *
 * El turno de espera del `closed` es por el orden en que Framework7 hace las cosas: el click de un
 * botón cierra el diálogo **antes** de llamar a su callback, así que sin animación el `closed`
 * llegaría primero y daría por abandonada una cadena que sí se había continuado.
 *
 * Lo que **no** llama a `alAbandonar` es el cierre de sesión (`cerrarLosDialogosDeOtraSesion`): ese
 * cerrojo es de la sesión anterior y lo suelta `abrir()` al reabrir la ventana, que es donde ya se
 * decidía lo mismo para el registro que se quedó en vuelo. Y por eso ese camino da la cadena por
 * terminada **él mismo** antes de cerrar: si no, su propio `closed` la abandonaría.
 *
 * @param app          el `$f7` de la pantalla, que es quien monta los diálogos
 * @param accion       la acción diferida, nombrada con `accionDeEstaSesion` ANTES del primer diálogo
 * @param texto        la pregunta
 * @param titulo       el título del diálogo
 * @param alContinuar  lo que pasa si se pulsa «Continuar» **y** la sesión sigue siendo la suya
 * @param alAbandonar  opcional: lo que pasa si esta cadena se acaba aquí (cancelar, u otra sesión)
 * @return la instancia del diálogo, o `null` si la acción ya no era de esta sesión y no se preguntó
 */
RhSolicitudesVacaciones.preguntarEnEstaSesion = function (app, accion, texto, titulo, alContinuar,
        alAbandonar) {
    const abandonar = function() {
        if (alAbandonar) alAbandonar();
    };
    if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
        abandonar();
        return null;
    }
    const abierto = { accion: accion, dialogo: null, resuelta: false };
    const olvidar = function() {
        const donde = dialogosDeAccionesDiferidas.indexOf(abierto);
        if (donde !== -1) dialogosDeAccionesDiferidas.splice(donde, 1);
    };
    // **Una cadena termina una sola vez**, y termina siempre: el apunte se quita y se hace lo que
    // toque por el camino que llegue primero —continuar, cancelar, el cierre del diálogo o el de la
    // sesión—. Que sea una sola es lo que permite escuchar el `closed` sin soltar dos veces el
    // cerrojo del llamante; que sea siempre, lo que evita que la lista crezca con diálogos que ya
    // no están en pantalla y que el cierre de sesión acabe cerrando instancias muertas.
    const resolver = function(queHacer) {
        if (abierto.resuelta) return;
        abierto.resuelta = true;
        olvidar();
        queHacer();
    };
    abierto.resolver = resolver;
    dialogosDeAccionesDiferidas.push(abierto);
    const continuar = function() {
        resolver(function() {
            // Y se vuelve a preguntar, aunque el cierre de sesión ya lo haya cerrado: cerrar el
            // diálogo es cosmético y el que decide sigue siendo el sello. Un diálogo que se
            // cerrara dejando encolado el callback de su botón llegaría aquí igual.
            if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
                abandonar();
                return;
            }
            alContinuar();
        });
    };
    // **Con `create` y sus dos botones, no con `confirm`**: `app.dialog.confirm` rotula los suyos
    // con `buttonOk` y `buttonCancel`, que son «OK» y «Cancel» y en esta aplicación nadie traduce
    // —no hay bloque `dialog` en los `f7params` de `app.vue`—, así que una pregunta en español
    // salía con los botones en inglés. Lo rechazó dirección el 18/10/2026 al probar la #70. Las
    // demás preguntas de la casa ya se montan así (p. ej. `calendario.vue#confirmarLimpieza*`).
    // El orden es el del `confirm` de Framework7: cancelar a la izquierda y continuar a la derecha,
    // en negrita, para no cambiar de sitio lo que la gente ya tiene aprendido a pulsar.
    abierto.dialogo = app.dialog.create({
        title: titulo,
        text: texto,
        buttons: [
            { text: ROTULOS_CONTINUAR.cancelar, keyCodes: [27], onClick: function() {
                resolver(abandonar);
            } },
            { text: ROTULOS_CONTINUAR.continuar, bold: true, keyCodes: [13], onClick: continuar }
        ]
    }).open();
    // Y el cierre del diálogo venga de donde venga, que es lo que cubre el Atrás de Android. Se
    // espera un turno porque Framework7 cierra antes de llamar al callback del botón: si lo que ha
    // cerrado el diálogo es una pulsación, para cuando esto corra ya estará resuelta por ella.
    if (abierto.dialogo && abierto.dialogo.on) {
        abierto.dialogo.on('closed', function() {
            Promise.resolve().then(function() { resolver(abandonar); });
        });
    }
    return abierto.dialogo;
};

/**
 * Cierra los «¿Continuar?» de las acciones que ya no son de la sesión que está delante. Lo llama el
 * cierre de sesión, **después** de subir el sello: antes, `sigueSiendoDeEstaSesion` diría que todas
 * siguen siendo suyas y no se cerraría ninguna.
 */
function cerrarLosDialogosDeOtraSesion() {
    dialogosDeAccionesDiferidas.slice().forEach(function(abierto) {
        if (RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(abierto.accion)) return;
        // Se da por terminada aquí, y **sin** llamar a `alAbandonar`: ese cerrojo es de la sesión
        // anterior y lo suelta `abrir()`. Antes de cerrar, para que el `closed` de este mismo
        // cierre no la abandone: la cadena ya está resuelta cuando llegue.
        abierto.resolver(function() {});
        try {
            if (abierto.dialogo && abierto.dialogo.close) abierto.dialogo.close();
        } catch (e) {
            // Un diálogo que no se deje cerrar no puede dejar a los demás abiertos.
            console.error('[RH-VAC] Fallo al cerrar un «¿Continuar?» de la sesión anterior', e);
        }
    });
}

/** Las escrituras del módulo que están en marcha ahora mismo, por su clave. */
const escriturasEnVuelo = new Set();

/**
 * **EL ÚNICO SITIO QUE IMPIDE QUE UNA ESCRITURA SALGA DOS VECES** (#58).
 *
 * Mientras una escritura con esa clave no haya terminado, otra igual no se lanza: se rechaza al
 * momento y sin mensaje. Las claves son `solicitar` —una solicitud a la vez por formulario— y
 * `anular|<id>`, que es por solicitud: anular dos distintas a la vez es legítimo.
 *
 * Esto salió dos veces en la misma revisión y en las dos escrituras: doble toque en «Enviar
 * solicitud» creaba **dos solicitudes idénticas** (r4229768214, un hallazgo que no llegó a
 * anunciarse en ninguna ronda), y doble toque en «Anular solicitud» mandaba dos anulaciones con la
 * misma `version` (ronda 10, r4236885085). En los dos casos el daño no es solo el duplicado: la
 * petición que pierde escribe su error —y apaga el reloj— **después** de que la otra haya ido bien,
 * así que la pantalla acaba diciendo que falló algo que funcionó.
 *
 * Vive aquí y no en el `data` de cada pantalla porque la pantalla es quien APAGA EL BOTÓN y eso no
 * se puede centralizar —cada una rotula el suyo, §9 del `CLAUDE.md` del orquestador—, pero que no
 * salga la petición sí: con el cerrojo solo en la pantalla, basta con que una escritura nueva (las
 * de la #59 y la #61) se olvide de llevarlo.
 *
 * Que es lo que pasó: aprobar y denegar (#59) salieron por `RhBandejaVacaciones` sin pasar por
 * aquí, y su única defensa contra el doble toque era el cerrojo de la pantalla —el que ha costado
 * tres rondas de revisión el 17/10/2026, una por cada sitio donde ese cerrojo no duraba lo que dura
 * el POST—. Desde esa fecha se exporta y las dos escrituras de la bandeja lo usan: la clave es
 * `resolver|<id>`, la misma para aprobar y para denegar, porque lo que no puede salir dos veces es
 * **una resolución de esa solicitud**, no cada verbo por su cuenta.
 */
export function unaSolaEnVuelo(clave, lanzar) {
    // **El cerrojo es de la sesión que lo coge**, y por eso la clave lleva su sello delante. Con una
    // clave global, cerrar sesión con un POST a medias dejaba `solicitar` ocupada para quien entrara
    // después en el mismo dispositivo: su «Enviar solicitud» se rechazaba como `yaEnVuelo` —un
    // rechazo que el formulario oculta a propósito, así que el botón parecía no hacer nada— y si la
    // petición de antes se quedaba colgada, no podía enviar en toda la sesión (revisión CODEX del PR
    // #1733, ronda 12). Va aquí dentro, y no en cada llamante, para que lo herede también lo que se
    // escriba mañana.
    const deEstaSesion = RhSolicitudesVacaciones.sesionActual() + '#' + clave;
    if (escriturasEnVuelo.has(deEstaSesion)) {
        return Promise.reject(rechazoYaEnVuelo());
    }
    // Y lo que cogió otra sesión se suelta al pasar por aquí: su respuesta ya no puede escribir nada
    // (`deLaSesionQueLaLanzo` la descarta), así que mantener su cerrojo solo serviría para que una
    // clave distinta no lo note y la cola crezca con sesiones que ya no existen.
    const miSello = RhSolicitudesVacaciones.sesionActual() + '#';
    escriturasEnVuelo.forEach(function (enVuelo) {
        if (enVuelo.indexOf(miSello) !== 0) escriturasEnVuelo.delete(enVuelo);
    });
    escriturasEnVuelo.add(deEstaSesion);
    const suelta = function() { escriturasEnVuelo.delete(deEstaSesion); };
    return lanzar().then(
        function(res) { suelta(); return res; },
        function(e) { suelta(); return Promise.reject(e); }
    );
}

/**
 * **LA PUERTA POR LA QUE SALE TODA ESCRITURA DE ESTE MÓDULO** (#69).
 *
 * Es `unaSolaEnVuelo` + `deLaSesionQueLaLanzo` en una sola llamada, y existe porque tenerlas
 * sueltas hacía que **apuntarse a las dos fuera cosa de quien escribiera la siguiente**. Las dos
 * garantías ya vivían en un solo sitio cada una, pero el alta era manual y por eso se repitió
 * cuatro veces: aprobar y denegar (#59) salieron sin ninguna de las dos, y pedir anulación y anular
 * directamente (#69) también —y con ellas, sus manejadores sin `sinNadaQueDecir`— (revisión CODEX
 * del PR #1748, ronda 6). Dos opciones por escritura nueva son dos casillas que se pueden quedar
 * sin marcar; una sola puerta no.
 *
 * Lo que garantiza, para cualquiera que pase por aquí:
 *
 *  - **que no salga dos veces** con la misma clave mientras la primera esté en vuelo, que es el
 *    doble toque en un botón que todavía no se ha apagado;
 *  - **que su respuesta no decida nada si delante hay otra sesión**, ni el éxito ni el fallo.
 *
 * La clave es la de `unaSolaEnVuelo` y se elige por **lo que no puede pasar dos veces**, no por el
 * verbo: `resolver|<id>` lo comparten aprobar, denegar, aceptar y rechazar la anulación y anular
 * directamente, porque las cinco son una resolución de ESA solicitud con la misma `version`.
 *
 * Y el guardarraíl que lo mantiene cerrado es `scripts/prueba-escrituras-del-modulo.js`: lee los
 * dos adaptadores y falla si aparece un POST que no sea una vista previa y no pase por aquí. Así la
 * escritura que se escriba mañana no puede nacer suelta sin que lo diga una prueba.
 */
export function escrituraDelModulo(clave, lanzar) {
    return unaSolaEnVuelo(clave, function() {
        return deLaSesionQueLaLanzo(lanzar());
    });
}

// GET /movilidad/rh/mis-vacaciones[?anio=N]
// → { username, nombre, anio, aniosDisponibles[], saldo{}, solicitudes[], aniosAnteriores[],
//     puedeSolicitar, impedimentos[], gestionadasEn, aprobador, autoconfirma }
// `saldo` llega DESGLOSADO y con sus `bolsas[]` en el orden en que se gastan (#57): el arrastre,
// los excepcionales de RRHH y el derecho del año, cada una con sus días, lo consumido, lo
// disponible y hasta cuándo vale. La pantalla las pinta; no recompone ninguna a base de restas.
// Todo lo que pinta la ventana en una sola petición: pedirlo por partes puede devolver fotos
// distintas —basta con que entre dos de ellas se apruebe algo— y dejaría el saldo sin cuadrar con
// las listas.
RhSolicitudesVacaciones.resumen = function(anio) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + (anio ? '?anio=' + anio : ''),
        method: 'GET'
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/mis-vacaciones/vista-previa  con [{desde, hasta}, …]
// → { computo: { dias[], diasExcluidos[], diasQueDescuenta, saldo{} }, trabajoPlanificado[] }
// Lo que enseña el formulario mientras se eligen las fechas. Lo calcula el servidor con las mismas
// cuentas que hará al enviar.
RhSolicitudesVacaciones.vistaPrevia = function(tramos) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/vista-previa',
        method: 'POST',
        data: tramos
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/mis-vacaciones/dia?fecha=YYYY-MM-DD → la solicitud de ese día, o null.
// La usa el detalle del día en el calendario: null no es un error, es un día de vacaciones que no
// pertenece a ninguna solicitud viva (los cargados a mano antes del módulo).
RhSolicitudesVacaciones.solicitudDelDia = function(fecha) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/dia?fecha=' + encodeURIComponent(fecha),
        method: 'GET'
    }, false).then(unwrapDto).then(function(solicitud) {
        // `unwrapDto` solo reconoce el envoltorio del ResponseDto si le llegan al menos dos de sus
        // campos, y con la inclusión NON_NULL que usa producción una respuesta sin solicitud se
        // queda en `{ list: [] }`: un objeto, y por tanto algo que la pantalla pintaría como si
        // fuera una solicitud sin código ni estado. La marca de que hay solicitud es su `codigo`.
        return solicitud && solicitud.codigo ? solicitud : null;
    });
};

// POST /movilidad/rh/vacaciones/solicitudes → la solicitud creada.
// Es el endpoint de la #57: aquí no se replica nada, se le manda lo que la persona eligió.
// Por la puerta del módulo (`escrituraDelModulo`): una a la vez y atada a la sesión que la lanzó.
RhSolicitudesVacaciones.solicitar = function(datos) {
    const self = this;
    return escrituraDelModulo('solicitar', function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes',
            method: 'POST',
            data: datos
        }, false).then(unwrapDto);
    });
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/anular → la solicitud ya anulada.
// El solicitante anula la suya mientras siga pendiente, al momento y sin motivo (D-ANU). La
// `version` es la que traía la solicitud al pintarla: es lo que corta dos resoluciones a la vez.
// Una a la vez POR SOLICITUD —anular dos distintas a la vez es legítimo— y atada a su sesión.
RhSolicitudesVacaciones.anular = function(id, version) {
    const self = this;
    return escrituraDelModulo('anular|' + id, function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/'
                + encodeURIComponent(id) + '/anular',
            method: 'POST',
            data: { version: version }
        }, false).then(unwrapDto);
    });
};

// POST /movilidad/rh/vacaciones/solicitudes/registrar/vista-previa?username=X  con [{desde,hasta}…]
// → { username, nombre, computo: { dias[], diasExcluidos[], diasQueDescuenta, saldo{},
//     cabenEnElSaldo, avisoSaldo }, saldoDespues, mesesCerrados[{anio, mes, cuadro, clave}] }
//
// Lo que enseña la ventana «Registrar en nombre de» (#70) antes de guardar. **Los dos avisos los
// calcula el servidor**: en qué saldo queda la persona —puede ser negativo— y qué meses de los
// cuadros de mando están cerrados. Aquí no se cuenta nada ni se decide qué mes está cerrado, igual
// que con el cómputo de la solicitud normal.
//
// No lleva cerrojo: es una lectura, y la ventana la lanza a cada cambio de fechas.
RhSolicitudesVacaciones.vistaPreviaRegistro = function(username, tramos) {
    const self = this;
    return deLaSesionQueLaLanzo(self.sendRequest({
        url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/registrar/vista-previa'
            + '?username=' + encodeURIComponent(username),
        method: 'POST',
        data: tramos
    }, false).then(unwrapDto));
};

// POST /movilidad/rh/vacaciones/solicitudes/registrar → la solicitud ya creada y CONFIRMADA.
//
// `mesesCerradosConfirmados` y `saldoNegativoConfirmado` son las dos respuestas a los avisos: el
// servidor rechaza el registro si hace falta alguna y no viene. Se mandan solo cuando la persona
// ha dicho «Continuar» en su diálogo, no por defecto: enviarlas siempre dejaría el aviso de
// adorno.
//
// Y las dos dicen **sobre qué** se dijo «Continuar» —los meses por la `clave` que trajo la vista
// previa (`OEB:2026-03`) y el saldo por el número que se enseñó—, no un sí a secas: entre la
// ventana y el guardado puede cerrarse otro mes de esas fechas, o entrar más días de esa persona y
// dejar el saldo peor, y lo aceptado era lo otro.
//
// Una a la vez POR PERSONA y atada a su sesión, como las otras dos escrituras del módulo: lo dice
// el contrato de `unaSolaEnVuelo`, que nombra esta escritura como una de las que se le podían
// olvidar. Registrar a dos personas a la vez es legítimo; dos veces a la misma, no: un doble toque
// dejaría dos solicitudes idénticas y el saldo descontado dos veces.
RhSolicitudesVacaciones.registrarEnNombreDe = function(datos) {
    const self = this;
    return escrituraDelModulo('registrar|' + (datos && datos.username), function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/registrar',
            method: 'POST',
            data: datos
        }, false).then(unwrapDto);
    });
};

// POST /movilidad/rh/vacaciones/solicitudes/{id}/anulacion/pedir → la solicitud con su petición.
//
// #69 · el solicitante PIDE anular días de unas vacaciones ya **confirmadas**. Es otra cosa que
// `anular`, que retira una pendiente al momento: aquí los días siguen en el calendario y siguen
// azules hasta que el aprobador acepte o rechace, así que esto no quita nada, solo deja pedido.
//
// Los días y el motivo son lo que la persona ha elegido; cuáles se pueden elegir lo dice el
// servidor en `diasAnulables`, no esta pantalla: el día de corte es el suyo —hora de Madrid— y el
// del navegador puede ir uno por delante o por detrás.
// Por la puerta del módulo, con la clave de `anular|<id>`: lo que no puede salir dos veces es que
// esta persona toque ESA solicitud suya, y pedir anular y retirarla son lo mismo a esos efectos.
RhSolicitudesVacaciones.pedirAnulacion = function(id, version, dias, motivo) {
    const self = this;
    return escrituraDelModulo('anular|' + id, function() {
        return self.sendRequest({
            url: self.server_movilidad + self.prefix + SOLICITUDES + '/solicitudes/'
                + encodeURIComponent(id) + '/anulacion/pedir',
            method: 'POST',
            data: { version: version, dias: dias, motivo: motivo }
        }, false).then(unwrapDto);
    });
};

// GET /movilidad/rh/normas-vacaciones/vigente → { version, fechaPublicacion, normas[] } o null.
//
// El endpoint es de la #56 y se llama desde aquí a propósito, en vez de depender de su módulo:
// las dos incidencias van por separado y esta pantalla solo necesita leer la versión vigente.
// `null` no es un error: es el estado del sistema antes de que la #56 publique su versión 1, y
// entonces el servidor tampoco exige haberlas leído (D-NOR).
RhSolicitudesVacaciones.normasVigentes = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + NORMAS + '/vigente',
        method: 'GET'
    }, false).then(unwrapDto);
};

export default RhSolicitudesVacaciones;
